import React, { useState, useEffect, useCallback, useMemo } from 'react';
import APIService from '../services/api';
// Le MÊME visualiseur que l'onglet des rapports du besoin : un second
// afficheur, c'est deux endroits où corriger la même faute de frappe, et
// deux rendus différents du même rapport selon l'écran d'où on l'ouvre.
import { TestReport } from './TestModule';

/**
 * Calendrier — la semaine de l'utilisateur, les deux moitiés réunies.
 *
 * Ce que l'ERP organise (entretiens, convocations, indisponibilités) ET ce que
 * son agenda d'entreprise contient déjà (Outlook & co, connecté par son lien
 * de calendrier). Un agenda qui ne montre que la moitié de la journée fait
 * poser des rendez-vous par-dessus des réunions : c'est l'union qui rend
 * l'écran digne de confiance.
 *
 * Trois choses que cet écran DIT toujours, parce que les taire rend un
 * calendrier menteur :
 *   • de QUAND datent les agendas connectés (une synchro qui a échoué ne
 *     s'affiche pas comme une journée libre) ;
 *   • d'OÙ vient chaque événement (le filet de couleur à gauche, dont la
 *     légende vient du serveur) ;
 *   • ce qui NE PEUT PAS être modifié d'ici (un agenda tiers est en lecture
 *     seule — le dire évite de chercher le bouton).
 *
 * Routes : GET /calendrier?du=&au=&tous= · GET /calendrier/fournisseurs ·
 *          GET|POST|PUT|DELETE /calendrier/abonnements ·
 *          POST /calendrier/abonnements/:id/sync
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const JOURS = ['LU', 'MA', 'ME', 'JE', 'VE', 'SA', 'DI'];

// Le lundi de la semaine d'une date — l'unité de travail d'un recruteur est la
// semaine, pas le mois.
const lundiDe = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const plusJours = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const memeJour = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const heure = (iso) => new Date(iso).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
const depuis = (iso) => {
  if (!iso) return 'jamais';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  if (min < 1440) return `il y a ${Math.round(min / 60)} h`;
  return new Date(iso).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' });
};

const VIDE_ABO = { nom: '', url: '', fournisseur: 'ics' };

export default function Calendrier({ showNotification, onOuvrirBesoin }) {
  const [lundi, setLundi] = useState(() => lundiDe(new Date()));
  const [tous, setTous] = useState(false);
  // Filtre « ce que l'agent mène » : le même agenda, restreint aux entretiens
  // que l'agent vocal conduit tout seul. C'est la question de fin de journée —
  // « qu'est-ce qui part sans moi ? ».
  const [appelant, setAppelant] = useState(null);
  // Le SERVEUR dit si la portée « toute l'agence » est permise (elle est
  // réservée aux administrateurs). L'écran ne le devine pas : il ne connaît
  // pas les droits, et un bouton qui échoue vaut moins qu'un bouton absent.
  const [porteeLargePermise, setPorteeLargePermise] = useState(false);
  // Le rapport d'un entretien passé, ouvert depuis sa pastille. C'est là qu'on
  // se demande comment ça s'est passé : on y est déjà, la date sous les yeux.
  const [rapport, setRapport] = useState(null);      // { chargement } | résultat
  const ouvrirRapport = useCallback(async (e) => {
    const id = e?.rapport?.session_id;
    if (id == null) return;
    setRapport({ chargement: true, titre: e.titre });
    try {
      const r = await fetch(`${BASE}/tests/session/${id}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setRapport({ resultat: j, titre: e.titre });
    } catch (err) {
      setRapport(null);
      showNotification?.(err.message, 'error');
    }
  }, [showNotification]);
  const [vue, setVue] = useState(null);        // null = chargement | 'absent'
  const [fournisseurs, setFournisseurs] = useState([]);
  const [ajout, setAjout] = useState(null);    // VIDE_ABO quand ouvert
  const [busy, setBusy] = useState(false);
  const [champErreur, setChampErreur] = useState(null);
  const [syncEnCours, setSyncEnCours] = useState(null);

  const dimanche = useMemo(() => plusJours(lundi, 7), [lundi]);

  const charger = useCallback(async () => {
    try {
      const q = new URLSearchParams({ du: lundi.toISOString(), au: dimanche.toISOString(),
        ...(tous ? { tous: '1' } : {}), ...(appelant ? { appelant } : {}) });
      const r = await fetch(`${BASE}/calendrier?${q}`, { headers: authH() });
      if ([404, 405, 501].includes(r.status)) { setVue('absent'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setVue(j);
      setPorteeLargePermise(j.portee_organisation_permise === true);
      // Le serveur a rendu une portée plus étroite que celle demandée : on le
      // DIT et on repasse le bouton sur « Mon agenda », plutôt que de laisser
      // un onglet actif qui ment sur ce qui est affiché.
      if (j.portee_refus) { showNotification?.(j.portee_refus, 'info'); setTous(false); }
    } catch (e) { showNotification?.(e.message, 'error'); setVue('absent'); }
  }, [lundi, dimanche, tous, appelant, showNotification]);
  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/calendrier/fournisseurs`, { headers: authH() });
        if (!r.ok) return;
        const j = await r.json().catch(() => ({}));
        if (Array.isArray(j.data)) setFournisseurs(j.data);
      } catch { /* l'écran reste utilisable sans le registre */ }
    })();
  }, []);

  const connecter = async () => {
    setChampErreur(null);
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/calendrier/abonnements`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify(ajout),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) {
        if (j.champ) { setChampErreur({ champ: String(j.champ), message: j.error }); return; }
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      showNotification?.(j.message || 'Calendrier connecté.', 'success');
      setAjout(null);
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const relire = async (id) => {
    setSyncEnCours(id);
    try {
      const r = await fetch(`${BASE}/calendrier/abonnements/${id}/sync`, { method: 'POST', headers: authH() });
      const j = await r.json().catch(() => ({}));
      // Le message du serveur, verbatim : c'est lui qui sait ce qui a été lu.
      if (!r.ok || j.success === false) showNotification?.(j.error || `HTTP ${r.status}`, 'error');
      else showNotification?.(j.message || 'Calendrier relu.', 'success');
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setSyncEnCours(null); }
  };

  const retirer = async (c) => {
    if (!window.confirm(`Retirer « ${c.nom} » ? Ses événements disparaîtront du calendrier.`)) return;
    try {
      const r = await fetch(`${BASE}/calendrier/abonnements/${c.id}`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Calendrier retiré.', 'success');
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
  };

  const evenements = useMemo(
    () => ((vue && vue !== 'absent' && Array.isArray(vue.evenements)) ? vue.evenements : []),
    [vue]);
  const calendriers = (vue && vue !== 'absent' && Array.isArray(vue.calendriers)) ? vue.calendriers : [];
  const sources = (vue && vue !== 'absent' && vue.sources) ? vue.sources : {};

  // Les sept colonnes, garnies. Le tout-le-jour d'abord : il tient la journée
  // entière, il ne se range pas entre deux horaires.
  const semaine = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const jour = plusJours(lundi, i);
    const dedans = evenements.filter(e => memeJour(new Date(e.debut), jour));
    return {
      jour,
      journee: dedans.filter(e => e.tout_journee),
      horaires: dedans.filter(e => !e.tout_journee),
    };
  }), [lundi, evenements]);

  const aujourdhui = new Date();
  const titre = `${lundi.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })} — ${plusJours(lundi, 6).toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })}`;
  const fournisseurChoisi = fournisseurs.find(f => f.id === ajout?.fournisseur) || null;

  return (
    <div className="cal-page">
      <header className="cal-tete">
        <div>
          <p className="cal-kicker">Mon calendrier</p>
          <h1>{titre}</h1>
          <p className="cal-sous">
            Vos entretiens et indisponibilités, plus les agendas que vous avez connectés.
          </p>
        </div>
        <div className="cal-actions">
          <div className="cal-nav" role="group" aria-label="Changer de semaine">
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setLundi(l => plusJours(l, -7))} aria-label="Semaine précédente"><BI icon="chevron-left" /></button>
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setLundi(lundiDe(new Date()))}>Aujourd'hui</button>
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setLundi(l => plusJours(l, 7))} aria-label="Semaine suivante"><BI icon="chevron-right" /></button>
          </div>
          <div className="cal-portee" role="group" aria-label="Portée du calendrier">
            <button type="button" className={`cal-portee-bt${!tous ? ' cal-portee-bt--on' : ''}`} onClick={() => setTous(false)}>Mon agenda</button>
            {/* « Toute l'agence » n'apparaît que pour qui y a droit — le
                serveur le dit. La barrière, elle, est côté serveur : ce
                bouton absent n'est qu'une politesse. */}
            {porteeLargePermise && (
              <button type="button" className={`cal-portee-bt${tous ? ' cal-portee-bt--on' : ''}`} onClick={() => setTous(true)}>Toute l'agence</button>
            )}
          </div>
          <div className="cal-portee" role="group" aria-label="Type d'entretien">
            <button type="button" className={`cal-portee-bt${!appelant ? ' cal-portee-bt--on' : ''}`} onClick={() => setAppelant(null)}>Tout</button>
            <button type="button" className={`cal-portee-bt${appelant === 'agent_ia' ? ' cal-portee-bt--on' : ''}`}
              title="Ne garder que les entretiens menés par l'agent vocal"
              onClick={() => setAppelant(a => (a === 'agent_ia' ? null : 'agent_ia'))}>Appels de l'agent</button>
          </div>
          {!ajout && <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={() => {
            // On ouvre sur le premier type DISPONIBLE : proposer d'emblée
            // celui qui refusera ferait cliquer pour rien.
            const dispo = fournisseurs.find(f => f.disponible);
            setAjout({ ...VIDE_ABO, fournisseur: dispo?.id || VIDE_ABO.fournisseur });
            setChampErreur(null);
          }}>
            <BI icon="calendar-plus" /> Connecter un calendrier
          </button>}
        </div>
      </header>

      {vue === 'absent' && (
        <div className="cal-note">Le calendrier arrive côté serveur — l'écran est prêt, rien n'est enregistré d'ici là.</div>
      )}

      {ajout && (
        <div className="cal-connexion">
          <div className="cal-connexion-grille">
            <label>Nom de ce calendrier
              <input className="erp-field" value={ajout.nom} placeholder="Outlook — mon agenda"
                style={champErreur?.champ === 'nom' ? { borderColor: 'var(--danger-bd)' } : undefined}
                onChange={e => { setAjout(a => ({ ...a, nom: e.target.value })); setChampErreur(null); }} />
            </label>
            <label>Type
              <select className="erp-field" value={ajout.fournisseur}
                onChange={e => { setAjout(a => ({ ...a, fournisseur: e.target.value })); setChampErreur(null); }}>
                {fournisseurs.map(f => (
                  <option key={f.id} value={f.id} disabled={!f.disponible}>
                    {f.libelle}{f.disponible ? '' : ' — pas encore disponible'}
                  </option>
                ))}
              </select>
            </label>
            {/* Le libellé et l'exemple viennent du serveur : Microsoft veut le
                COMPTE de la personne, un lien ICS veut une URL. L'écran ne
                devine pas — il afficherait « adresse du calendrier » devant un
                champ où il faut taper une adresse email. */}
            <label className="cal-connexion-large">{fournisseurChoisi?.champ_libelle || 'Adresse du calendrier'}
              <input className="erp-field erp-mono" value={ajout.url}
                placeholder={fournisseurChoisi?.champ_exemple || 'https://outlook.office365.com/owa/calendar/…/calendar.ics'}
                style={champErreur?.champ === 'url' ? { borderColor: 'var(--danger-bd)' } : undefined}
                onChange={e => { setAjout(a => ({ ...a, url: e.target.value })); setChampErreur(null); }} />
            </label>
          </div>
          {/* L'aide vient du serveur : c'est lui qui sait ce qui est branché et
              ce qui manque encore. */}
          {fournisseurChoisi?.aide && <p className="cal-aide">{fournisseurChoisi.aide}</p>}
          {champErreur && <p className="cal-erreur" role="alert">{champErreur.message}</p>}
          <div className="cal-connexion-pied">
            <span className="cal-hint">Lecture seule : ERPIA affiche cet agenda, il n'y écrit jamais.</span>
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => { setAjout(null); setChampErreur(null); }}>Annuler</button>
            <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={connecter} disabled={busy || !fournisseurChoisi?.disponible}>
              <BI icon={busy ? 'arrow-repeat' : 'plug'} style={busy ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
              {busy ? 'Connexion…' : 'Connecter'}
            </button>
          </div>
        </div>
      )}

      {vue === null ? (
        <div className="cal-vide"><BI icon="arrow-repeat" /> Chargement de la semaine…</div>
      ) : vue !== 'absent' ? (
        <>
          <div className="cal-semaine" role="table" aria-label={`Semaine du ${titre}`}>
            {semaine.map(({ jour, journee, horaires }, i) => {
              const cejour = memeJour(jour, aujourdhui);
              const passe = jour < aujourdhui && !cejour;
              return (
                <div key={i} className={`cal-jour${cejour ? ' cal-jour--auj' : ''}${passe ? ' cal-jour--passe' : ''}`} role="cell">
                  <div className="cal-jour-tete">
                    <span className="cal-jour-nom">{JOURS[i]}</span>
                    <span className="cal-jour-num">{jour.getDate()}</span>
                  </div>
                  {journee.map(e => <Chip key={e.id} e={e} journee onOuvrir={ouvrirRapport} onOuvrirBesoin={onOuvrirBesoin} />)}
                  {horaires.map(e => <Chip key={e.id} e={e} onOuvrir={ouvrirRapport} onOuvrirBesoin={onOuvrirBesoin} />)}
                  {!journee.length && !horaires.length && <div className="cal-jour-rien" aria-hidden="true" />}
                </div>
              );
            })}
          </div>

          <div className="cal-legende" aria-label="Origine des événements">
            {Object.entries(sources).map(([cle, s]) => (
              <span key={cle} className="cal-legende-item">
                <i className={`cal-filet cal-filet--${cle}`} aria-hidden="true" />
                {s.libelle} <span className="cal-legende-org">· {s.origine}</span>
              </span>
            ))}
          </div>

          {rapport && (rapport.chargement ? (
            <div className="cal-hint" role="status"><BI icon="arrow-repeat" /> Ouverture du rapport…</div>
          ) : (
            <TestReport result={rapport.resultat}
              candidat={{ prenom: rapport.resultat?.candidatNom || '', nom: '' }}
              besoin={{ titre: rapport.resultat?.besoinTitre || '', client: rapport.resultat?.besoinClient || '' }}
              onClose={() => setRapport(null)} />
          ))}

          <section className="cal-agendas">
            <h2 className="cal-agendas-titre">Agendas connectés</h2>
            {!calendriers.length ? (
              <p className="cal-hint">
                Aucun agenda connecté. Collez le lien de partage de votre calendrier Outlook
                pour voir vos réunions ici — ERPIA le lit, il n'y écrit pas.
              </p>
            ) : calendriers.map(c => (
              <div key={c.id} className={`cal-agenda${c.dernier_ok === false ? ' cal-agenda--ko' : ''}`}>
                <div className="cal-agenda-qui">
                  <strong>{c.nom}</strong>
                  <span className="cal-agenda-type">{c.fournisseur_libelle}</span>
                </div>
                {/* La date de synchro n'est pas un détail technique : sans elle,
                    « rien mardi » ne veut rien dire. */}
                <div className="cal-agenda-etat">
                  <BI icon={c.dernier_ok === false ? 'exclamation-triangle-fill' : 'check2-circle'} />
                  {c.dernier_ok === false ? 'Non relu' : 'Synchronisé'} {depuis(c.derniere_sync)}
                  {c.dernier_message && <span className="cal-agenda-msg">{c.dernier_message}</span>}
                </div>
                <div className="cal-agenda-bts">
                  <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => relire(c.id)} disabled={syncEnCours === c.id}>
                    <BI icon="arrow-repeat" style={syncEnCours === c.id ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
                    {syncEnCours === c.id ? 'Lecture…' : 'Relire'}
                  </button>
                  <button type="button" className="erp-btn erp-btn--danger erp-btn--sm" onClick={() => retirer(c)}>Retirer</button>
                </div>
              </div>
            ))}
            {vue.calendriers_erreur && <p className="cal-erreur" role="alert">{vue.calendriers_erreur}</p>}
          </section>
        </>
      ) : null}
    </div>
  );
}

// Un événement. Le filet de gauche encode son ORIGINE — c'est la seule
// couleur de l'écran, et elle dit quelque chose de vrai.
// Ce qu'un événement passé a laissé derrière lui. Le serveur sert `rapport`
// sur chaque entretien mené par l'agent : `null` = rien à ouvrir (pas encore
// passé, ou évaluation manquante). On ne rend cliquable que ce qui s'ouvre —
// un chip qui réagit au clic pour ne rien montrer se réessaie trois fois.
const rapportOuvrable = e => !!(e?.rapport && e.rapport.session_id != null);
// Un appel PASSÉ sans évaluation garde quand même son enregistrement et sa
// transcription. Le laisser muet ferait renoncer à la seule trace qui reste —
// on le dit, sans promettre un rapport qui n'existe pas.
const traceSeule = e => !!(e?.rapport && e.rapport.session_id == null && (e.rapport.ecoute || e.rapport.transcript));

function Chip({ e, journee = false, onOuvrir, onOuvrirBesoin }) {
  const ouvrable = rapportOuvrable(e);
  const r = e.rapport;
  // SUR QUEL BESOIN ? Vécu 22.09 : « quand on clique sur un appel à venir,
  // ouvre le profil sur le besoin, ou écris au moins le nom du profil et du
  // besoin — là ce n'est pas clair. » La pastille disait « Appel de l'agent —
  // Ihsan Sarikaya » et rien d'autre : on sait QUI est appelé, jamais POUR
  // QUOI. Sur une semaine où trois besoins tournent, ça ne se devine pas — et
  // le serveur envoyait déjà le besoin, personne ne l'affichait.
  const besoin = [e.besoin_titre, e.client].filter(Boolean).join(' · ');
  const versBesoin = e.besoin_id != null && onOuvrirBesoin ? () => onOuvrirBesoin(e.besoin_id) : null;
  const infos = [e.titre, besoin, e.lieu].filter(Boolean).join(' — ');
  const contenu = (<>
    <span className="cal-ev-h erp-mono">{journee ? 'Journée' : heure(e.debut)}</span>
    <span className="cal-ev-t">{e.titre}</span>
    {besoin && <span className="cal-ev-l">{besoin}</span>}
    {e.lieu && <span className="cal-ev-l"><BI icon="geo-alt" /> {e.lieu}</span>}
    {e.calendrier && <span className="cal-ev-src">{e.calendrier}</span>}
    {/* Le score sur la pastille : c'est l'information qu'on cherche en
        regardant un entretien passé — pas besoin d'ouvrir pour la lire. */}
    {ouvrable && (
      <span className="cal-ev-src erp-mono">
        <BI icon="file-earmark-text" /> {r.score != null ? `${Math.round(r.score)}%` : 'rapport'}
        {r.recommandation ? ` · ${r.recommandation}` : ''}
      </span>
    )}
    {/* LE PETIT RÉSUMÉ : les premières phrases de la synthèse, à même la
        pastille. C'est ce qu'on veut lire avant de décider s'il faut ouvrir le
        rapport complet — et le plus souvent ça suffit. */}
    {ouvrable && r.resume && <span className="cal-ev-resume">{r.resume}</span>}
    {traceSeule(e) && (
      <span className="cal-ev-src">
        <BI icon={e.rapport.ecoute_url ? 'play-circle' : 'mic'} /> Appel passé — {e.rapport.ecoute_url
          ? 'écouter l\'enregistrement'
          : (e.rapport.transcript ? 'transcription disponible' : 'enregistrement disponible')}, pas encore évalué
      </span>
    )}
  </>);

  if (!ouvrable) {
    // UN APPEL PASSÉ SANS ÉVALUATION n'a pas de rapport à ouvrir — mais il a
    // son enregistrement, et c'est la seule trace qui reste. La pastille
    // devient alors le lien d'écoute : le calendrier savait qu'un
    // enregistrement existait et n'avait rien à ouvrir (un témoin allumé sans
    // interrupteur). Le rapport, lui, porte l'écoute dans son corps.
    if (r?.ecoute_url) {
      return (
        <a className={`cal-ev cal-ev--${e.source}${journee ? ' cal-ev--journee' : ''} cal-ev--ouvrable`}
          href={r.ecoute_url} target="_blank" rel="noreferrer"
          title={`${infos} — écouter l'enregistrement`}
          style={{ display: 'block', textAlign: 'left', width: '100%', font: 'inherit', color: 'inherit', textDecoration: 'none' }}>
          {contenu}
        </a>
      );
    }
    // RIEN À OUVRIR CÔTÉ RAPPORT — un appel À VENIR, une convocation, un
    // entretien de demain. Ce qu'on cherche alors, c'est le DOSSIER : le
    // besoin, avec le profil dessus. La pastille y mène ; sans besoin
    // rattaché (une indisponibilité), elle reste ce qu'elle était.
    if (versBesoin) {
      return (
        <button type="button" className={`cal-ev cal-ev--${e.source}${journee ? ' cal-ev--journee' : ''} cal-ev--ouvrable`}
          title={`${infos} — ouvrir le besoin`}
          onClick={versBesoin}
          style={{ textAlign: 'left', width: '100%', font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
          {contenu}
        </button>
      );
    }
    return (
      <div className={`cal-ev cal-ev--${e.source}${journee ? ' cal-ev--journee' : ''}`} title={infos}>
        {contenu}
      </div>
    );
  }
  return (
    <button type="button" className={`cal-ev cal-ev--${e.source}${journee ? ' cal-ev--journee' : ''} cal-ev--ouvrable`}
      title={`${infos} — ouvrir le rapport de l'entretien`}
      onClick={() => onOuvrir?.(e)}
      style={{ textAlign: 'left', width: '100%', font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
      {contenu}
    </button>
  );
}
